import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  getBranches,
  getBranch,
  createBranch,
  updateBranch,
  deleteBranch,
  toggleBranchStatus,
  sendSmsToBranch,
  sendBulkSmsToBranches,
  getBranchStats,
  exportBranches,
  clearApiMessage,
  clearError,
  clearBranchState,
  selectBranches,
  selectBranchPagination,
  selectCurrentBranch,
  selectBranchStats,
  selectBranchStatsSummary,
  selectBranchesLoading,
  selectBranchesSubmitting,
  selectBranchesApiMessage,
  selectBranchesError,
} from '../../store/slices/system/branchSlice';
import {
  Plus,
  Pencil,
  Save,
  Trash2,
  AlertTriangle,
  Send,
  MessageSquare,
  BarChart3,
  ClipboardList,
  Search,
  Church,
  Users,
  CheckCircle2,
  Loader2,
  Eye,
  Phone,
  Check,
  Pause,
  Play,
} from 'lucide-react';

const MultiBranch = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [currentBranch, setCurrentBranch] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [editingBranchId, setEditingBranchId] = useState(null);

  // ==================== REFS ====================
  const editNameRef = useRef(null);
  const editPastorRef = useRef(null);
  const editPhoneRef = useRef(null);
  const editEmailRef = useRef(null);
  const editAddressRef = useRef(null);
  const editMembersRef = useRef(null);
  const editActiveRef = useRef(null);

  const addNameRef = useRef(null);
  const addPastorRef = useRef(null);
  const addPhoneRef = useRef(null);
  const addEmailRef = useRef(null);
  const addAddressRef = useRef(null);
  const addMembersRef = useRef(null);
  const addActiveRef = useRef(null);

  const smsMessageRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const branches = useSelector(selectBranches) || [];
  const pagination = useSelector(selectBranchPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const stats = useSelector(selectBranchStats);
  const statsSummary = useSelector(selectBranchStatsSummary);
  const loading = useSelector(selectBranchesLoading);
  const isSubmitting = useSelector(selectBranchesSubmitting);
  const apiMessage = useSelector(selectBranchesApiMessage);
  const error = useSelector(selectBranchesError);

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          dispatch(getBranches({ 
            per_page: itemsPerPage, 
            page: currentPage 
          })),
          dispatch(getBranchStats()),
        ]);
      } catch (err) {
        console.error('Error loading branches:', err);
      }
    };
    
    loadData();
    
    return () => {
      dispatch(clearBranchState());
    };
  }, [dispatch]);

  // Handle search and pagination
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };
      
      dispatch(getBranches(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, currentPage, itemsPerPage, dispatch]);

  // Display API messages - with deduplication (same as Members component)
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;
    
    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== HELPER FUNCTIONS ====================

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // ==================== PAGINATION ====================

  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;
  const currentPageNum = pagination?.current_page || 1;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== BRANCH CRUD ====================

  // Add new branch
  const handleAddBranch = () => {
    setTimeout(() => {
      if (addNameRef.current) addNameRef.current.value = '';
      if (addPastorRef.current) addPastorRef.current.value = '';
      if (addPhoneRef.current) addPhoneRef.current.value = '';
      if (addEmailRef.current) addEmailRef.current.value = '';
      if (addAddressRef.current) addAddressRef.current.value = '';
      if (addMembersRef.current) addMembersRef.current.value = '0';
      if (addActiveRef.current) addActiveRef.current.checked = true;
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Plus size={16} style={{ verticalAlign: '-3px' }} /> Add New Branch</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Branch Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" ref={addNameRef} placeholder="e.g. Grace Chapel - East Legon" />
          </div>
          <div className="form-group">
            <label className="form-label">Pastor Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" ref={addPastorRef} placeholder="e.g. Rev. John Doe" />
          </div>
          <div className="form-group">
            <label className="form-label">Phone Number <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" ref={addPhoneRef} placeholder="e.g. +233244123456" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" ref={addEmailRef} placeholder="e.g. branch@church.com" type="email" />
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-input" ref={addAddressRef} placeholder="e.g. 123 Main Street, Accra" />
          </div>
          <div className="form-group">
            <label className="form-label">Initial Members</label>
            <input className="form-input" type="number" ref={addMembersRef} placeholder="0" min="0" defaultValue="0" />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" ref={addActiveRef} defaultChecked={true} />
              Active Branch
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const name = addNameRef.current?.value?.trim() || '';
              const pastor = addPastorRef.current?.value?.trim() || '';
              const phone = addPhoneRef.current?.value?.trim() || '';
              const email = addEmailRef.current?.value?.trim() || '';
              const address = addAddressRef.current?.value?.trim() || '';
              const members = parseInt(addMembersRef.current?.value) || 0;
              const is_active = addActiveRef.current?.checked || false;

              if (!name) {
                showToastMessage('Please enter a branch name', 'error');
                return;
              }
              if (!pastor) {
                showToastMessage('Please enter a pastor name', 'error');
                return;
              }
              if (!phone) {
                showToastMessage('Please enter a phone number', 'error');
                return;
              }

              try {
                const result = await dispatch(createBranch({
                  name,
                  pastor_name: pastor,
                  phone,
                  email,
                  address,
                  member_count: members,
                  is_active
                })).unwrap();
                
                if (result.success) {
                  closeModal();
                  dispatch(getBranches({ 
                    per_page: itemsPerPage, 
                    page: currentPage,
                    search: searchTerm
                  }));
                  dispatch(getBranchStats());
                  showToastMessage(`✅ "${name}" branch added!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to create branch', 'error');
              }
            }}
          >
            {isSubmitting ? 'Adding...' : <><Plus size={16} style={{ verticalAlign: '-3px' }} /> Add Branch</>}
          </button>
        </div>
      </div>
    );
  };

  // Edit branch
  const handleEditBranch = (branch) => {
    setEditingBranchId(branch.id);
    
    setTimeout(() => {
      if (editNameRef.current) editNameRef.current.value = branch.name || '';
      if (editPastorRef.current) editPastorRef.current.value = branch.pastor || '';
      if (editPhoneRef.current) editPhoneRef.current.value = branch.phone || '';
      if (editEmailRef.current) editEmailRef.current.value = branch.email || '';
      if (editAddressRef.current) editAddressRef.current.value = branch.address || '';
      if (editMembersRef.current) editMembersRef.current.value = branch.member_count || 0;
      if (editActiveRef.current) editActiveRef.current.checked = branch.is_active !== false;
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Branch</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Branch Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" ref={editNameRef} defaultValue="" />
          </div>
          <div className="form-group">
            <label className="form-label">Pastor Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" ref={editPastorRef} defaultValue="" />
          </div>
          <div className="form-group">
            <label className="form-label">Phone Number <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" ref={editPhoneRef} defaultValue="" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" ref={editEmailRef} defaultValue="" type="email" />
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-input" ref={editAddressRef} defaultValue="" />
          </div>
          <div className="form-group">
            <label className="form-label">Members</label>
            <input className="form-input" type="number" ref={editMembersRef} defaultValue="0" min="0" />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" ref={editActiveRef} defaultChecked={true} />
              Active Branch
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const name = editNameRef.current?.value?.trim() || '';
              const pastor = editPastorRef.current?.value?.trim() || '';
              const phone = editPhoneRef.current?.value?.trim() || '';
              const email = editEmailRef.current?.value?.trim() || '';
              const address = editAddressRef.current?.value?.trim() || '';
              const member_count = parseInt(editMembersRef.current?.value) || 0;
              const is_active = editActiveRef.current?.checked || false;

              if (!name) {
                showToastMessage('Please enter a branch name', 'error');
                return;
              }
              if (!pastor) {
                showToastMessage('Please enter a pastor name', 'error');
                return;
              }
              if (!phone) {
                showToastMessage('Please enter a phone number', 'error');
                return;
              }

              try {
                const result = await dispatch(updateBranch({
                  id: branch.id,
                  data: {
                    name,
                    pastor_name: pastor,
                    phone,
                    email,
                    address,
                    member_count,
                    is_active
                  }
                })).unwrap();
                
                if (result.success) {
                  closeModal();
                  dispatch(getBranches({ 
                    per_page: itemsPerPage, 
                    page: currentPage,
                    search: searchTerm
                  }));
                  dispatch(getBranchStats());
                  showToastMessage(`✅ "${name}" branch updated!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to update branch', 'error');
              }
            }}
          >
            {isSubmitting ? 'Saving...' : <><Save size={16} style={{ verticalAlign: '-3px' }} /> Save Changes</>}
          </button>
        </div>
      </div>
    );
  };

  // Delete branch
  const handleDeleteBranch = (branch) => {
    if (branches.length <= 1) {
      showToastMessage('⚠️ Cannot delete the only branch', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Branch</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{branch.name}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> This will remove the branch and all its associated data.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              try {
                await dispatch(deleteBranch(branch.id)).unwrap();
                closeModal();
                dispatch(getBranches({ 
                  per_page: itemsPerPage, 
                  page: currentPage,
                  search: searchTerm
                }));
                dispatch(getBranchStats());
                showToastMessage(`🗑️ "${branch.name}" branch deleted`, 'error');
              } catch (err) {
                showToastMessage(err.message || 'Failed to delete branch', 'error');
              }
            }}
          >
            Delete Branch
          </button>
        </div>
      </div>
    );
  };

  // Toggle branch status
  const handleToggleBranch = async (branch) => {
    try {
      await dispatch(toggleBranchStatus(branch.id)).unwrap();
      dispatch(getBranches({ 
        per_page: itemsPerPage, 
        page: currentPage,
        search: searchTerm
      }));
      dispatch(getBranchStats());
      showToastMessage(`✅ Branch ${branch.is_active ? 'deactivated' : 'activated'}`, 'success');
    } catch (err) {
      showToastMessage(err.message || 'Failed to toggle branch status', 'error');
    }
  };

  // ==================== SEND SMS ====================

  const handleSendSMS = (branch) => {
    setTimeout(() => {
      if (smsMessageRef.current) {
        smsMessageRef.current.value = `Hello {FirstName}, this message is for ${branch.name}. — Grace Chapel`;
      }
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Send size={16} style={{ verticalAlign: '-3px' }} /> Send SMS to {branch.name}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ 
            padding: '12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
              Send SMS to <strong>{branch.member_count || 0}</strong> members of <strong>{branch.name}</strong>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Message <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea 
              className="form-textarea" 
              rows="4" 
              ref={smsMessageRef}
              defaultValue={`Hello {FirstName}, this message is for ${branch.name}. — Grace Chapel`}
            />
            <div className="form-hint">Use {'{FirstName}'} for personalisation</div>
          </div>
          <div style={{ 
            padding: '10px 12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <span><BarChart3 size={13} style={{ verticalAlign: '-2px' }} /> {branch.member_count || 0} recipients</span>
            <span><MessageSquare size={13} style={{ verticalAlign: '-2px' }} /> {smsMessageRef.current?.value?.length || 0} characters</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const message = smsMessageRef.current?.value?.trim() || '';
              if (!message) {
                showToastMessage('Please enter a message', 'error');
                return;
              }

              try {
                const result = await dispatch(sendSmsToBranch({
                  id: branch.id,
                  message: message,
                  // sender_id: 'CHURCH'
                })).unwrap();
                
                if (result.success) {
                  closeModal();
                  dispatch(getBranches({ 
                    per_page: itemsPerPage, 
                    page: currentPage,
                    search: searchTerm
                  }));
                  showToastMessage(`📨 SMS sent to ${branch.name}!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to send SMS', 'error');
              }
            }}
          >
            <Send size={16} style={{ verticalAlign: '-3px' }} /> Send SMS
          </button>
        </div>
      </div>
    );
  };

  // ==================== CROSS-BRANCH BROADCAST ====================

  const handleCrossBranchBroadcast = () => {
    if (!broadcastMessage.trim()) {
      showToastMessage('Please enter a message', 'error');
      return;
    }

    const selectedBranchList = branches.filter(b => selectedBranches.includes(b.id));
    if (selectedBranchList.length === 0) {
      showToastMessage('Please select at least one branch', 'error');
      return;
    }

    const totalRecipients = selectedBranchList.reduce((sum, b) => sum + (b.member_count || 0), 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Send size={16} style={{ verticalAlign: '-3px' }} /> Confirm Cross-Branch Broadcast</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ 
            padding: '12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Message Preview</div>
            <div style={{ fontSize: '13px', color: 'var(--text)' }}>{broadcastMessage}</div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--navy)', marginBottom: '8px' }}>
              Selected Branches ({selectedBranchList.length})
            </div>
            {selectedBranchList.map(b => (
              <div key={b.id} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                padding: '6px 0',
                borderBottom: '1px solid var(--border-light)',
                fontSize: '13px'
              }}>
                <span>{b.name}</span>
                <span style={{ color: 'var(--text-light)' }}>{b.member_count || 0} members</span>
              </div>
            ))}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              padding: '8px 0',
              borderTop: '2px solid var(--border)',
              fontSize: '13px',
              fontWeight: '600'
            }}>
              <span>Total Recipients</span>
              <span>{totalRecipients.toLocaleString()}</span>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              try {
                const result = await dispatch(sendBulkSmsToBranches({
                  branchIds: selectedBranches,
                  message: broadcastMessage,
                  sender_id: 'CHURCH'
                })).unwrap();
                
                if (result.success) {
                  closeModal();
                  setBroadcastMessage('');
                  setSelectedBranches([]);
                  dispatch(getBranches({ 
                    per_page: itemsPerPage, 
                    page: currentPage,
                    search: searchTerm
                  }));
                  dispatch(getBranchStats());
                  showToastMessage(`📨 Broadcast sent to ${selectedBranchList.length} branches (${totalRecipients} members)!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to send broadcast', 'error');
              }
            }}
          >
            <Send size={16} style={{ verticalAlign: '-3px' }} /> Send Broadcast
          </button>
        </div>
      </div>
    );
  };

  // ==================== VIEW DETAILS ====================

  const handleViewDetails = (branch) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><ClipboardList size={16} style={{ verticalAlign: '-3px' }} /> Branch Details</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Branch Name</span>
              <span style={{ fontWeight: '600' }}>{branch.name}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Pastor</span>
              <span>{branch.pastor}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Phone</span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{branch.phone}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Email</span>
              <span>{branch.email || 'Not set'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Address</span>
              <span>{branch.address || 'Not set'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Members</span>
              <span style={{ fontWeight: '600' }}>{branch.member_count || 0}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>SMS Sent</span>
              <span>{branch.sms_count || 0}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Status</span>
              <span>
                <span className={`badge ${branch.is_active ? 'badge-green' : 'badge-amber'}`}>
                  {branch.is_active ? 'Active' : 'Inactive'}
                </span>
              </span>
            </div>
            {branch.created_at && (
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Created</span>
                <span>{formatDate(branch.created_at)}</span>
              </div>
            )}
            {branch.updated_at && branch.updated_at !== branch.created_at && (
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Last Updated</span>
                <span>{formatDate(branch.updated_at)}</span>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button className="btn btn-gold btn-sm" onClick={() => {
            closeModal();
            setTimeout(() => handleSendSMS(branch), 100);
          }}>
            <Send size={16} style={{ verticalAlign: '-3px' }} /> Send SMS
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => {
            closeModal();
            setTimeout(() => handleEditBranch(branch), 100);
          }}>
            <Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit
          </button>
        </div>
      </div>
    );
  };

  // ==================== SELECTION ====================

  const toggleBranchSelection = (branchId) => {
    if (selectedBranches.includes(branchId)) {
      setSelectedBranches(selectedBranches.filter(id => id !== branchId));
    } else {
      setSelectedBranches([...selectedBranches, branchId]);
    }
  };

  const toggleAllBranches = () => {
    if (selectedBranches.length === branches.length) {
      setSelectedBranches([]);
    } else {
      setSelectedBranches(branches.map(b => b.id));
    }
  };

  // ==================== STATS ====================

  const statsData = statsSummary || stats || { 
    total_branches: 0, 
    total_members: 0, 
    total_sms: 0,
    active_branches: 0,
    inactive_branches: 0
  };

  // ==================== RENDER ====================

  const selectedBranchList = branches.filter(b => selectedBranches.includes(b.id));
  const totalRecipients = selectedBranchList.reduce((sum, b) => sum + (b.member_count || 0), 0);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          Manage multiple church campuses from one dashboard.
          {currentBranch && (
            <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--gold)' }}>
              · Active: {currentBranch.name}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <div className="search-box" style={{ maxWidth: '200px' }}>
            <span className="search-icon"><Search size={14} /></span>
            <input 
              type="text" 
              placeholder="Search branches..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="btn btn-gold btn-sm" onClick={handleAddBranch} disabled={loading}>
            <Plus size={16} style={{ verticalAlign: '-3px' }} /> Add Branch
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold">
          <div className="stat-label">Total Branches</div>
          <div className="stat-value">{statsData.total_branches || 0}</div>
          <div className="stat-icon"><Church size={20} /></div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Total Members</div>
          <div className="stat-value">{statsData.total_members?.toLocaleString() || 0}</div>
          <div className="stat-icon"><Users size={20} /></div>
        </div>
        <div className="stat-card blue">
          <div className="stat-label">SMS Sent</div>
          <div className="stat-value">{statsData.total_sms?.toLocaleString() || 0}</div>
          <div className="stat-icon"><Send size={20} /></div>
        </div>
        <div className="stat-card purple">
          <div className="stat-label">Active Branches</div>
          <div className="stat-value">{statsData.active_branches || 0}/{statsData.total_branches || 0}</div>
          <div className="stat-icon"><CheckCircle2 size={20} /></div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', marginBottom: '20px' }}>
        {loading && branches.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0' }}>
            <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
            <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading branches...</div>
          </div>
        ) : branches.length > 0 ? (
          branches.map(b => (
            <div key={b.id} className="card" style={{ 
              borderTop: `3px solid ${b.is_active ? 'var(--gold)' : 'var(--text-light)'}`,
              opacity: b.is_active ? 1 : 0.7
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><Church size={22} /></div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--navy)' }}>{b.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{b.pastor}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button 
                    className="btn btn-ghost btn-sm btn-icon" 
                    onClick={() => handleViewDetails(b)}
                    title="View Details"
                    disabled={loading}
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => handleEditBranch(b)}
                    title="Edit"
                    disabled={loading}
                  >
                    <Pencil size={16} />
                  </button>
                  <button 
                    className="btn btn-ghost btn-sm btn-icon" 
                    onClick={() => handleToggleBranch(b)}
                    title={b.is_active ? 'Deactivate' : 'Activate'}
                    disabled={loading}
                  >
                    {b.is_active ? <Pause size={16} /> : <Play size={16} />}
                  </button>
                  <button 
                    className="btn btn-ghost btn-sm btn-icon" 
                    onClick={() => handleDeleteBranch(b)}
                    title="Delete"
                    style={{ color: 'var(--red)' }}
                    disabled={loading || branches.length <= 1}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{b.member_count || 0}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Members</div>
                </div>
                <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{b.sms_count || 0}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>SMS Sent</div>
                </div>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '12px' }}>
                <Phone size={12} style={{ verticalAlign: '-2px' }} /> {b.phone}
                {b.is_active && <span style={{ marginLeft: '8px', color: 'var(--green)' }}>● Active</span>}
                {!b.is_active && <span style={{ marginLeft: '8px', color: 'var(--text-light)' }}>● Inactive</span>}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button className="btn btn-gold btn-sm" style={{ flex: 1 }} onClick={() => handleSendSMS(b)} disabled={loading}>
                  <Send size={14} style={{ verticalAlign: '-2px' }} /> SMS Branch
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setCurrentBranch(b)}>
                  {currentBranch?.id === b.id ? <><Check size={14} style={{ verticalAlign: '-2px' }} /> Active</> : 'Switch →'}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div style={{ gridColumn: '1 / -1' }}>
            <div className="empty-state">
              <div className="empty-icon"><Church size={40} strokeWidth={1.5} /></div>
              <div className="empty-title">No branches found</div>
              <div className="empty-text">Try adjusting your search or add a new branch</div>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          marginTop: '20px',
          padding: '16px',
          background: 'var(--white)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            Showing <strong>{((currentPageNum - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(currentPageNum * itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong> branches
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
              <select 
                className="form-select" 
                style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                disabled={loading}
              >
                <option value={4}>4</option>
                <option value={8}>8</option>
                <option value={12}>12</option>
                <option value={24}>24</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '4px' }}>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={() => goToPage(currentPageNum - 1)}
                disabled={currentPageNum === 1 || loading}
                style={{ opacity: currentPageNum === 1 ? 0.5 : 1 }}
              >
                ← Prev
              </button>
              
              {getPageNumbers().map(page => (
                <button 
                  key={page}
                  className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => goToPage(page)}
                  disabled={loading}
                >
                  {page}
                </button>
              ))}
              
              <button 
                className="btn btn-ghost btn-sm"
                onClick={() => goToPage(currentPageNum + 1)}
                disabled={currentPageNum === totalPages || loading}
                style={{ opacity: currentPageNum === totalPages ? 0.5 : 1 }}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cross-Branch Broadcast */}
      <div className="card" style={{ marginTop: '20px' }}>
        <div className="card-header">
          <div className="card-title">Cross-Branch Broadcast</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <span className="badge badge-blue">
              {selectedBranches.length} Branches Selected
            </span>
            <button 
              className="btn btn-ghost btn-sm" 
              onClick={toggleAllBranches}
              disabled={loading}
            >
              {selectedBranches.length === branches.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>
        <div className="grid-2">
          <div>
            <div className="form-group">
              <label className="form-label">Select Branches</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', background: 'var(--cream)', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                {branches.map(b => (
                  <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                    <input 
                      type="checkbox" 
                      checked={selectedBranches.includes(b.id)}
                      onChange={() => toggleBranchSelection(b.id)}
                      style={{ accentColor: 'var(--navy)' }}
                      disabled={loading}
                    /> 
                    {b.name} 
                    <span style={{ color: 'var(--text-light)', fontSize: '11px' }}>({b.member_count || 0} members)</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Message</label>
              <textarea 
                className="form-textarea" 
                rows="3" 
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Hello {FirstName}, this message goes to all branches…" 
                disabled={loading}
              />
            </div>
          </div>
          <div>
            <div style={{ background: 'var(--navy)', borderRadius: '12px', padding: '16px', marginBottom: '14px' }}>
              <div style={{ fontSize: '11px', color: 'var(--gold)', marginBottom: '8px', fontWeight: '600' }}>
                BROADCAST SUMMARY
              </div>
              {selectedBranchList.map(b => (
                <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,.7)', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
                  <span>{b.name}</span>
                  <span>{b.member_count || 0}</span>
                </div>
              ))}
              {selectedBranchList.length === 0 && (
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,.4)', textAlign: 'center', padding: '12px 0' }}>
                  No branches selected
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: '700', color: 'var(--gold-light)', padding: '8px 0 0', marginTop: '4px', borderTop: '1px solid rgba(255,255,255,.1)' }}>
                <span>Total</span>
                <span>{totalRecipients.toLocaleString()}</span>
              </div>
            </div>
            <button 
              className="btn btn-gold btn-sm" 
              style={{ width: '100%' }}
              onClick={handleCrossBranchBroadcast}
              disabled={selectedBranches.length === 0 || !broadcastMessage.trim() || loading || isSubmitting}
            >
              <Send size={16} style={{ verticalAlign: '-3px' }} /> Send to {selectedBranches.length} Branch{selectedBranches.length !== 1 ? 'es' : ''}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-blue {
          background: var(--blue-bg);
          color: var(--blue);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .badge-red {
          background: var(--red-bg);
          color: var(--red);
        }
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .stat-card.purple .stat-icon {
          color: var(--purple);
        }
        .stat-card.purple .stat-value {
          color: var(--purple);
        }
        .btn-primary {
          background: var(--gold);
          color: var(--navy);
          border: none;
        }
        .btn-primary:hover {
          background: var(--gold-dark);
        }
        .form-hint {
          font-size: 11px;
          color: var(--text-light);
          margin-top: 4px;
        }
        .btn-sm {
          padding: 4px 10px;
          font-size: 12px;
        }
        .btn-icon {
          padding: 4px 6px;
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
          }
        }
        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
};

export default MultiBranch;